// src/components/kit/microsite/MicrositeRenderer.tsx
//
// Core rendering component for microsite pages. Takes a PublicMicrositeData
// or MicrositeFormState and renders the complete microsite with the chosen
// layout, colors, fonts, and content.
//
// All generated CSS is scoped to the .ms-root container so that styles
// do not leak into the parent application when used in the builder preview.

import { useState } from 'react';
import type { PublicMicrositeData, MicrositeFormState, MicrositeLink } from '@/types/microsite';
import { LINK_CATEGORIES, STREAMING_CATEGORIES, SVG_ICONS, getIconSvg } from './themes';

interface MicrositeRendererProps {
  data: PublicMicrositeData | MicrositeFormState;
  onLinkClick?: (linkId: string, label: string) => void;
  onEmailSubmit?: (email: string) => Promise<void>;
  isPreview?: boolean;
}

// ---- Helpers ----

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatEventDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function buildGoogleFontsUrl(headingFont: string, bodyFont: string): string {
  const fonts = new Set([headingFont, bodyFont]);
  const families = [...fonts]
    .map((f) => {
      const slug = f.replace(/ /g, '+');
      return `family=${slug}:wght@300;400;500;600;700;800;900`;
    })
    .join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

function getStreamingLinks(links: MicrositeLink[]): MicrositeLink[] {
  return links.filter((l) => STREAMING_CATEGORIES.includes(l.category));
}

function getNonStreamingLinks(links: MicrositeLink[]): MicrositeLink[] {
  return links.filter((l) => !STREAMING_CATEGORIES.includes(l.category));
}

// ---- CSS Generation ----

function buildCSSVariables(data: PublicMicrositeData | MicrositeFormState): string {
  const c = data.colors;
  const br = data.border_radius;
  return `
    --color-bg: ${c.background};
    --color-surface: ${c.surface};
    --color-primary: ${c.primary};
    --color-accent: ${c.accent};
    --color-text: ${c.text};
    --color-text-muted: ${c.textMuted};
    --color-button-bg: ${c.buttonBg};
    --color-button-text: ${c.buttonText};
    --color-accent-10: ${hexToRgba(c.accent, 0.1)};
    --color-accent-20: ${hexToRgba(c.accent, 0.2)};
    --color-bg-90: ${hexToRgba(c.background, 0.9)};
    --color-bg-80: ${hexToRgba(c.background, 0.8)};
    --color-bg-70: ${hexToRgba(c.background, 0.7)};
    --color-bg-50: ${hexToRgba(c.background, 0.5)};
    --color-bg-30: ${hexToRgba(c.background, 0.3)};
    --color-text-muted-15: ${hexToRgba(c.textMuted, 0.15)};
    --hero-scrim: #000;
    --color-button-bg-10: ${hexToRgba(c.buttonBg, 0.1)};
    --color-button-bg-20: ${hexToRgba(c.buttonBg, 0.2)};
    --font-heading: '${data.fonts.headingFont}', sans-serif;
    --font-body: '${data.fonts.bodyFont}', sans-serif;
    --base-font-size: ${data.base_font_size}px;
    --border-radius: ${br}px;
    --border-radius-sm: ${Math.max(4, Math.round(br * 0.5))}px;
    --border-radius-lg: ${Math.round(br * 1.5)}px;
  `;
}

function heroPlaceholderCSS(data: PublicMicrositeData | MicrositeFormState): string {
  const c = data.colors;
  return `linear-gradient(135deg, ${hexToRgba(c.accent, 0.27)} 0%, ${c.background} 50%, ${hexToRgba(c.primary, 0.2)} 100%)`;
}

/** Generate button-style-specific CSS properties for .ms-link-btn. */
function buttonStyleProperties(style: string): string {
  if (style === 'outlined') {
    return 'background: transparent; color: var(--color-button-bg); border: 2px solid var(--color-button-bg);';
  } else if (style === 'ghost') {
    return 'background: transparent; color: var(--color-primary); border: none; text-decoration: underline; text-underline-offset: 4px;';
  } else if (style === 'soft') {
    return 'background: var(--color-button-bg-10); color: var(--color-button-bg); border: none;';
  }
  // filled (default)
  return 'background: var(--color-button-bg); color: var(--color-button-text); border: none;';
}

/** Generate button-style-specific hover CSS for .ms-link-btn:hover. */
function buttonStyleHoverProperties(style: string): string {
  if (style === 'outlined') {
    return 'background: var(--color-button-bg); color: var(--color-button-text);';
  } else if (style === 'ghost') {
    return 'color: var(--color-accent);';
  } else if (style === 'soft') {
    return 'background: var(--color-button-bg-20);';
  }
  // filled (default)
  return 'opacity: 0.88; transform: translateY(-1px);';
}

// ---- The full CSS for the microsite ----
// All rules are scoped under .ms-root so they don't leak into the
// parent app when rendered inside the builder preview.

function buildFullCSS(data: PublicMicrositeData | MicrositeFormState): string {
  const cssVars = buildCSSVariables(data);
  const btnProps = buttonStyleProperties(data.button_style);
  const btnHoverProps = buttonStyleHoverProperties(data.button_style);
  const heroPlaceholder = heroPlaceholderCSS(data);

  return `
/* Scoped root — all microsite styles live under .ms-root */
.ms-root {
  ${cssVars}
  font-family: var(--font-body);
  font-size: var(--base-font-size);
  background: var(--color-bg);
  color: var(--color-text);
  line-height: 1.6;
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
}
.ms-root *, .ms-root *::before, .ms-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.ms-root img { max-width: 100%; display: block; }
.ms-root a { color: inherit; text-decoration: none; }

@supports (color: lch(from red l c h)) {
  .ms-root {
    /* light text -> black scrim, dark text -> white scrim */
    --hero-scrim: lch(from var(--color-text) calc((l - 50) * -1000) 0 0);
  }
}

/* Typography */
.ms-root h1, .ms-root h2, .ms-root h3 { font-family: var(--font-heading); line-height: 1.2; }
.ms-artist-name {
  font-size: clamp(1.8rem, 5vw, 3.2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--color-text);
  text-shadow: 0 1px 2px color-mix(in srgb, var(--hero-scrim) 50%, transparent),
               0 2px 14px color-mix(in srgb, var(--hero-scrim) 45%, transparent);
}
.ms-tagline {
  font-size: clamp(0.85rem, 2vw, 1.1rem);
  color: var(--color-text-muted);
  margin-top: 0.3em;
  font-weight: 400;
  letter-spacing: 0.04em;
  text-shadow: 0 1px 2px color-mix(in srgb, var(--hero-scrim) 50%, transparent),
               0 1px 10px color-mix(in srgb, var(--hero-scrim) 40%, transparent);
}
.ms-section-title {
  font-size: 1.15rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-muted);
}
.ms-section-title-icon { width: 1.1em; height: 1.1em; flex-shrink: 0; }
.ms-section-title-icon svg { width: 100%; height: 100%; }

/* Profile Image: Circle mode */
.ms-profile-img {
  width: 100px; height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--color-accent);
  margin-bottom: 0.75rem;
}
.ms-profile-placeholder {
  width: 100px; height: 100px;
  border-radius: 50%;
  background: var(--color-surface);
  margin-bottom: 0.75rem;
  border: 3px solid var(--color-accent);
}

/* Profile Image: Full / Logo mode — consistent sizing across all viewports */
.ms-profile-img-full {
  width: 80%; max-width: 400px; min-width: 120px;
  height: auto; object-fit: contain;
  margin-bottom: 1rem;
}

/* Hero (Bold & Card Grid) */
.ms-hero {
  position: relative; width: 100%; min-height: 55vh;
  overflow: hidden; display: flex; align-items: center; justify-content: center;
}
.ms-hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.ms-hero-placeholder { position: absolute; inset: 0; background: ${heroPlaceholder}; }
.ms-hero-overlay {
  position: relative; z-index: 2; width: 100%; height: 100%; min-height: 55vh;
  display: flex; align-items: center; justify-content: center; padding: 2rem;
  background: radial-gradient(ellipse at center, var(--color-bg-70) 0%, var(--color-bg-30) 60%, transparent 100%);
}
.ms-hero-content {
  max-width: 700px; text-align: center;
  display: flex; flex-direction: column; align-items: center;
}

/* Hero bottom fade — smooth transition into page background */
.ms-hero::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 35%;
  background: linear-gradient(to bottom, transparent 0%, var(--color-bg) 100%);
  z-index: 3;
  pointer-events: none;
}

/* Main Content */
.ms-main-content { max-width: 700px; margin: 0 auto; padding: 1.5rem 1.5rem 3rem; }

/* Listen Button */
.ms-listen-section { margin-bottom: 2rem; display: flex; justify-content: center; }
.ms-listen-wrapper { position: relative; width: 100%; max-width: 340px; }
.ms-listen-btn {
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 0.6rem;
  padding: 0.85rem 1.5rem; font-family: var(--font-heading); font-size: 1.05rem; font-weight: 700;
  background: var(--color-accent); color: var(--color-button-text); border: none;
  border-radius: var(--border-radius); cursor: pointer; transition: all 0.2s; letter-spacing: 0.03em;
}
.ms-listen-btn:hover { opacity: 0.9; transform: translateY(-1px); }
.ms-listen-btn-icon svg { width: 1.2em; height: 1.2em; }
.ms-listen-btn-chevron svg { width: 1em; height: 1em; transition: transform 0.25s; }
.ms-listen-wrapper.open .ms-listen-btn-chevron svg { transform: rotate(180deg); }
.ms-listen-menu {
  display: none; flex-direction: column; margin-top: 0.5rem;
  background: var(--color-surface); border-radius: var(--border-radius);
  overflow: hidden; box-shadow: 0 8px 30px rgba(0,0,0,0.18);
}
.ms-listen-wrapper.open .ms-listen-menu { display: flex; }
.ms-listen-menu-item {
  display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem 1.2rem;
  font-size: 0.95rem; font-weight: 500; transition: background 0.15s; color: var(--color-text);
  cursor: pointer; border: none; background: none; text-align: left; width: 100%;
  font-family: var(--font-body);
}
.ms-listen-menu-item:hover { background: var(--color-accent-10); }
.ms-listen-menu-icon { width: 1.3em; height: 1.3em; flex-shrink: 0; }
.ms-listen-menu-icon svg { width: 100%; height: 100%; }

/* Bio */
.ms-bio-section { margin-bottom: 2rem; text-align: center; }
.ms-bio-text { font-size: 0.95rem; color: var(--color-text-muted); line-height: 1.7; max-width: 560px; margin: 0 auto; }

/* Featured */
.ms-featured-section {
  background: var(--color-surface); border-radius: var(--border-radius-lg);
  overflow: hidden; margin-bottom: 2rem; display: flex; flex-direction: column;
}
.ms-featured-section-link {
  cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
}
.ms-featured-section-link:hover {
  transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}
.ms-featured-img-wrap { max-height: 220px; overflow: hidden; }
.ms-featured-img { width: 100%; height: 100%; object-fit: cover; }
.ms-featured-text { padding: 1.25rem 1.5rem; text-align: center; }
.ms-featured-title { font-size: 1.3rem; font-weight: 700; color: var(--color-primary); }
.ms-featured-subtitle { font-size: 0.9rem; color: var(--color-text-muted); margin-top: 0.3rem; }

/* Links (stacked) — button style properties are merged directly */
.ms-links-section { margin-bottom: 2rem; }
.ms-links-list { display: flex; flex-direction: column; gap: 0.65rem; }
.ms-link-btn {
  display: flex; align-items: center; gap: 0.75rem; padding: 0.8rem 1.2rem;
  border-radius: var(--border-radius); font-family: var(--font-body);
  font-size: 0.95rem; font-weight: 600; cursor: pointer; transition: all 0.2s; text-align: left;
  ${btnProps}
}
.ms-link-btn:hover { ${btnHoverProps} }
.ms-link-icon { width: 1.3em; height: 1.3em; flex-shrink: 0; }
.ms-link-icon svg { width: 100%; height: 100%; }
.ms-link-thumb { width: 36px; height: 36px; border-radius: var(--border-radius-sm); object-fit: cover; flex-shrink: 0; }
.ms-link-label { flex: 1; }

/* Card Grid */
.ms-card-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
.ms-link-card {
  display: flex; flex-direction: column; align-items: center;
  background: var(--color-surface); border-radius: var(--border-radius);
  overflow: hidden; transition: all 0.2s; cursor: pointer; text-align: center;
}
.ms-link-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.15); }
.ms-card-thumb {
  width: 100%; aspect-ratio: 1; overflow: hidden;
  display: flex; align-items: center; justify-content: center; background: var(--color-accent-10);
}
.ms-card-thumb img { width: 100%; height: 100%; object-fit: cover; }
.ms-card-thumb-icon { color: var(--color-accent); }
.ms-card-thumb-icon svg { width: 2.5rem; height: 2.5rem; }
.ms-card-label { padding: 0.7rem 0.5rem; font-weight: 600; font-size: 0.85rem; }

/* Events */
.ms-events-section { margin-bottom: 2rem; }
.ms-events-list { display: flex; flex-direction: column; gap: 0; }
.ms-event-row {
  display: flex; align-items: center; gap: 1rem; padding: 0.9rem 0;
  border-bottom: 1px solid var(--color-text-muted-15);
}
.ms-event-row:last-child { border-bottom: none; }
.ms-event-date {
  font-family: var(--font-heading); font-weight: 700; font-size: 0.85rem;
  color: var(--color-accent); min-width: 90px; white-space: nowrap;
}
.ms-event-info { flex: 1; min-width: 0; }
.ms-event-venue { font-weight: 600; font-size: 0.9rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ms-event-city { font-size: 0.8rem; color: var(--color-text-muted); }
.ms-event-ticket-btn {
  padding: 0.4rem 1rem; font-size: 0.8rem; font-weight: 600;
  border: 2px solid var(--color-accent); border-radius: var(--border-radius);
  color: var(--color-accent); white-space: nowrap; transition: all 0.2s;
  background: none; cursor: pointer; text-decoration: none; display: inline-block;
  font-family: var(--font-body);
}
.ms-event-ticket-btn:hover { background: var(--color-accent); color: var(--color-button-text); }

/* Email Capture */
.ms-email-section {
  background: var(--color-surface); border-radius: var(--border-radius-lg);
  padding: 1.25rem 1.5rem; margin-bottom: 2rem; text-align: center;
}
.ms-email-prompt { font-size: 0.95rem; font-weight: 600; margin-bottom: 0.75rem; color: var(--color-text); }
.ms-email-form { display: flex; gap: 0.5rem; flex-wrap: wrap; justify-content: center; }
.ms-email-input {
  flex: 1; min-width: 180px; padding: 0.6rem 1rem; font-size: 0.9rem;
  border: 1px solid var(--color-text-muted-15); border-radius: var(--border-radius);
  background: var(--color-bg); color: var(--color-text); outline: none;
  font-family: var(--font-body);
}
.ms-email-input:focus { border-color: var(--color-accent); }
.ms-email-submit {
  padding: 0.6rem 1.5rem; font-size: 0.9rem; font-weight: 600;
  background: var(--color-accent); color: var(--color-button-text);
  border: none; border-radius: var(--border-radius); cursor: pointer; transition: all 0.2s;
  font-family: var(--font-body);
}
.ms-email-submit:hover { opacity: 0.9; }
.ms-email-success { color: var(--color-accent); font-weight: 600; font-size: 0.9rem; }
.ms-email-error { color: #ef4444; font-size: 0.8rem; margin-top: 0.5rem; }

/* Footer */
.ms-site-footer { text-align: center; padding: 2rem 0 1rem; font-size: 1.2rem; color: var(--color-text-muted); }
.ms-site-footer a { color: var(--color-accent); text-decoration: none; }
.ms-site-footer a:hover { text-decoration: underline; }

/* Split Layout */
.ms-layout-split { display: flex; min-height: 100vh; }
.ms-split-left {
  position: sticky; top: 0; width: 45%; height: 100vh;
  overflow: hidden; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ms-split-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.ms-split-img-placeholder { position: absolute; inset: 0; background: ${heroPlaceholder}; }
.ms-split-left-overlay {
  position: relative; z-index: 2; width: 100%; padding: 2rem;
  background: radial-gradient(ellipse at center, var(--color-bg-70) 0%, var(--color-bg-30) 60%, transparent 100%);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; min-height: 100vh;
}
.ms-split-right { flex: 1; padding: 2.5rem 2rem 3rem; overflow-y: auto; max-width: 600px; }
.ms-split-right-inner { max-width: 100%; }

/* Card Grid layout adjustments */
.ms-layout-card-grid .ms-hero { min-height: 45vh; }
.ms-layout-card-grid .ms-hero-overlay { min-height: 45vh; }

/* Responsive */
@media (max-width: 768px) {
  .ms-layout-split { flex-direction: column; }
  .ms-split-left { position: relative; width: 100%; height: 55vh; }
  .ms-split-left-overlay { min-height: 55vh; }
  .ms-split-right { padding: 1.5rem; max-width: none; }
  .ms-hero { min-height: 45vh; }
  .ms-hero-overlay { min-height: 45vh; }
  .ms-card-grid { grid-template-columns: repeat(2, 1fr); }
  .ms-event-row { flex-wrap: wrap; gap: 0.5rem; }
  .ms-event-date { min-width: auto; }
}
@media (max-width: 420px) {
  /* Card grid stays at 2 columns on small screens */
  .ms-card-grid { grid-template-columns: repeat(2, 1fr); gap: 0.5rem; }
  .ms-main-content { padding: 1rem 1rem 2rem; }
  .ms-profile-img, .ms-profile-placeholder { width: 80px; height: 80px; }
}
`;
}

// ============================================================
// React Component
// ============================================================

export default function MicrositeRenderer({
  data,
  onLinkClick,
  onEmailSubmit,
  isPreview = false,
}: MicrositeRendererProps) {
  const [listenOpen, setListenOpen] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');

  const fontsUrl = buildGoogleFontsUrl(data.fonts.headingFont, data.fonts.bodyFont);
  const fullCSS = buildFullCSS(data);

  const streamingLinks = getStreamingLinks(data.links);
  const nonStreamingLinks = getNonStreamingLinks(data.links);

  const showHeroArtistName = data.show_hero_artist_name !== false;

  const handleLinkClick = (link: MicrositeLink) => {
    if (isPreview) return;
    onLinkClick?.(link.link_id, link.label);
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const handleStreamingClick = (link: MicrositeLink) => {
    if (isPreview) return;
    onLinkClick?.(link.link_id, link.label);
    setListenOpen(false);
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const handleFeaturedClick = () => {
    if (isPreview || !data.featured_url) return;
    onLinkClick?.('featured', data.featured_title || 'Featured');
    window.open(data.featured_url, '_blank', 'noopener,noreferrer');
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPreview || !onEmailSubmit) return;
    const trimmed = emailValue.trim();
    if (!trimmed) return;
    setEmailStatus('loading');
    setEmailError('');
    try {
      await onEmailSubmit(trimmed);
      setEmailStatus('success');
      setEmailValue('');
    } catch (err: any) {
      setEmailStatus('error');
      setEmailError(err?.error?.message || 'Something went wrong');
    }
  };

  // ---- Shared section renderers ----

  const renderProfileImage = () => {
    const isFullMode = data.profile_display_mode === 'full';
    const imgUrl = data.profile_image_url;

    if (isFullMode) {
      if (imgUrl) {
        return <img src={imgUrl} alt={data.artist_name} className="ms-profile-img-full" />;
      }
      return null;
    }

    if (imgUrl) {
      return <img src={imgUrl} alt={data.artist_name} className="ms-profile-img" />;
    }
    return <div className="ms-profile-placeholder" />;
  };

  const renderListenButton = () => {
    if (streamingLinks.length === 0) return null;
    return (
      <div className="ms-listen-section">
        <div className={`ms-listen-wrapper${listenOpen ? ' open' : ''}`}>
          <button className="ms-listen-btn" onClick={() => setListenOpen(!listenOpen)}>
            <span className="ms-listen-btn-icon" dangerouslySetInnerHTML={{ __html: SVG_ICONS.headphones }} />
            <span>Listen Now</span>
            <span className="ms-listen-btn-chevron" dangerouslySetInnerHTML={{ __html: SVG_ICONS.chevronDown }} />
          </button>
          <div className="ms-listen-menu">
            {streamingLinks.map((link) => {
              const cat = LINK_CATEGORIES.find((c) => c.id === link.category);
              const iconSvg = getIconSvg(cat?.icon || 'music');
              return (
                <button
                  key={link.link_id}
                  className="ms-listen-menu-item"
                  onClick={() => handleStreamingClick(link)}
                >
                  <span className="ms-listen-menu-icon" dangerouslySetInnerHTML={{ __html: iconSvg }} />
                  <span>{link.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderBio = () => {
    if (!data.bio) return null;
    return (
      <section className="ms-bio-section">
        <p className="ms-bio-text">{data.bio}</p>
      </section>
    );
  };

  const renderFeatured = () => {
    if (!data.featured_title) return null;

    const hasLink = !!data.featured_url;
    const sectionClass = `ms-featured-section${hasLink ? ' ms-featured-section-link' : ''}`;

    const content = (
      <>
        {data.featured_image_url && (
          <div className="ms-featured-img-wrap">
            <img src={data.featured_image_url} alt="" className="ms-featured-img" />
          </div>
        )}
        <div className="ms-featured-text">
          <h2 className="ms-featured-title">{data.featured_title}</h2>
          {data.featured_subtitle && <p className="ms-featured-subtitle">{data.featured_subtitle}</p>}
        </div>
      </>
    );

    if (hasLink) {
      return (
        <section
          className={sectionClass}
          onClick={handleFeaturedClick}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleFeaturedClick(); }}
        >
          {content}
        </section>
      );
    }

    return (
      <section className={sectionClass}>
        {content}
      </section>
    );
  };

  const renderStackedLinks = () => {
    if (nonStreamingLinks.length === 0) return null;
    return (
      <section className="ms-links-section">
        <div className="ms-links-list">
          {nonStreamingLinks.map((link) => {
            const cat = LINK_CATEGORIES.find((c) => c.id === link.category);
            const iconSvg = getIconSvg(cat?.icon || 'link');
            return (
              <button
                key={link.link_id}
                className="ms-link-btn"
                onClick={() => handleLinkClick(link)}
              >
                {link.image_url && <img src={link.image_url} alt="" className="ms-link-thumb" />}
                <span className="ms-link-icon" dangerouslySetInnerHTML={{ __html: iconSvg }} />
                <span className="ms-link-label">{link.label}</span>
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  const renderCardGridLinks = () => {
    if (nonStreamingLinks.length === 0) return null;
    return (
      <section className="ms-links-section">
        <div className="ms-card-grid">
          {nonStreamingLinks.map((link) => {
            const cat = LINK_CATEGORIES.find((c) => c.id === link.category);
            const iconSvg = getIconSvg(cat?.icon || 'link');
            return (
              <button
                key={link.link_id}
                className="ms-link-card"
                onClick={() => handleLinkClick(link)}
              >
                {link.image_url ? (
                  <div className="ms-card-thumb"><img src={link.image_url} alt="" /></div>
                ) : (
                  <div className="ms-card-thumb ms-card-thumb-icon" dangerouslySetInnerHTML={{ __html: iconSvg }} />
                )}
                <span className="ms-card-label">{link.label}</span>
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  const renderEvents = () => {
    if (!data.show_events || !data.events || data.events.length === 0) return null;
    return (
      <section className="ms-events-section">
        <h2 className="ms-section-title">
          <span className="ms-section-title-icon" dangerouslySetInnerHTML={{ __html: SVG_ICONS.calendar }} />
          Upcoming Shows
        </h2>
        <div className="ms-events-list">
          {data.events.map((evt) => (
            <div key={evt.event_id} className="ms-event-row">
              <div className="ms-event-date">{formatEventDate(evt.date)}</div>
              <div className="ms-event-info">
                <div className="ms-event-venue">{evt.venue}</div>
                <div className="ms-event-city">{evt.city}</div>
              </div>
              {evt.ticket_url && (
                <a
                  href={evt.ticket_url}
                  className="ms-event-ticket-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (isPreview) e.preventDefault();
                  }}
                >
                  Tickets
                </a>
              )}
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderEmailCapture = () => {
    if (!data.email_capture?.enabled) return null;
    return (
      <section className="ms-email-section">
        <p className="ms-email-prompt">{data.email_capture.prompt_text || 'Join my mailing list'}</p>
        {emailStatus === 'success' ? (
          <p className="ms-email-success">✓ You're subscribed!</p>
        ) : (
          <form className="ms-email-form" onSubmit={handleEmailSubmit}>
            <input
              type="email"
              className="ms-email-input"
              placeholder="your@email.com"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              disabled={isPreview}
            />
            <button type="submit" className="ms-email-submit" disabled={isPreview || emailStatus === 'loading'}>
              {emailStatus === 'loading' ? '...' : data.email_capture.button_label || 'Subscribe'}
            </button>
          </form>
        )}
        {emailStatus === 'error' && emailError && (
          <p className="ms-email-error">{emailError}</p>
        )}
      </section>
    );
  };

  const renderFooter = () => (
    <footer className="ms-site-footer">
      <p>
        <a
          href="https://www.bandjerry.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => isPreview && e.preventDefault()}
        >
          Powered by BandJerry
        </a>
      </p>
    </footer>
  );

  // ---- Main content (shared across layouts) ----
  // Order: Bio → Featured → Listen Now → Links → Events → Email → Footer

  const renderMainContent = (useCardGrid: boolean) => (
    <main className="ms-main-content">
      {renderBio()}
      {renderFeatured()}
      {renderListenButton()}
      {useCardGrid ? renderCardGridLinks() : renderStackedLinks()}
      {renderEvents()}
      {renderEmailCapture()}
      {renderFooter()}
    </main>
  );

  // ---- Hero section (Bold & Card Grid) ----

  const renderHero = () => {
    const heroImg = data.hero_image_url
      ? <img src={data.hero_image_url} alt="" className="ms-hero-img" />
      : <div className="ms-hero-placeholder" />;

    return (
      <section className="ms-hero">
        {heroImg}
        <div className="ms-hero-overlay">
          <div className="ms-hero-content">
            {renderProfileImage()}
            {showHeroArtistName && data.artist_name && <h1 className="ms-artist-name">{data.artist_name}</h1>}
            {data.tagline && <p className="ms-tagline">{data.tagline}</p>}
          </div>
        </div>
      </section>
    );
  };

  // ---- Layout renderers ----

  const renderBoldLayout = () => (
    <div className="ms-layout-bold">
      {renderHero()}
      {renderMainContent(false)}
    </div>
  );

  const renderSplitLayout = () => {
    const heroImg = data.hero_image_url
      ? <img src={data.hero_image_url} alt="" className="ms-split-img" />
      : <div className="ms-split-img-placeholder" />;

    return (
      <div className="ms-layout-split">
        <aside className="ms-split-left">
          {heroImg}
          <div className="ms-split-left-overlay">
            {renderProfileImage()}
            {showHeroArtistName && data.artist_name && <h1 className="ms-artist-name">{data.artist_name}</h1>}
            {data.tagline && <p className="ms-tagline">{data.tagline}</p>}
          </div>
        </aside>
        <main className="ms-split-right">
          <div className="ms-split-right-inner">
            {renderBio()}
            {renderFeatured()}
            {renderListenButton()}
            {renderStackedLinks()}
            {renderEvents()}
            {renderEmailCapture()}
            {renderFooter()}
          </div>
        </main>
      </div>
    );
  };

  const renderCardGridLayout = () => (
    <div className="ms-layout-card-grid">
      {renderHero()}
      {renderMainContent(true)}
    </div>
  );

  // ---- Choose layout ----

  let layoutContent: React.ReactNode;
  switch (data.layout) {
    case 'split':
      layoutContent = renderSplitLayout();
      break;
    case 'cardGrid':
      layoutContent = renderCardGridLayout();
      break;
    case 'bold':
    default:
      layoutContent = renderBoldLayout();
      break;
  }

  return (
    <div className="ms-root">
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href={fontsUrl} rel="stylesheet" />
      <style dangerouslySetInnerHTML={{ __html: fullCSS }} />
      {layoutContent}
    </div>
  );
}
